const urlBase = process.env.GATSBY_API_URL;

const GET_CACHE_TTL_MS = 1500;
const inFlightGetRequests = new Map<string, Promise<any>>();
const recentGetResponses = new Map<string, { expiresAt: number; data: any }>();

const getJsonWithDedup = async (
  key: string,
  request: () => Promise<Response>
) => {
  const now = Date.now();
  const cached = recentGetResponses.get(key);

  if (cached && cached.expiresAt > now) {
    return cached.data;
  }

  const inFlight = inFlightGetRequests.get(key);
  if (inFlight) {
    return await inFlight;
  }

  const pending = (async () => {
    const resp = await request();
    const data = await resp.json();
    recentGetResponses.set(key, {
      expiresAt: Date.now() + GET_CACHE_TTL_MS,
      data,
    });
    return data;
  })();

  inFlightGetRequests.set(key, pending);

  try {
    return await pending;
  } finally {
    inFlightGetRequests.delete(key);
  }
};

const getAuthToken = () => localStorage.getItem("token") || "";

const clearGetCacheByPath = (path: string) => {
  for (const key of recentGetResponses.keys()) {
    if (key.includes(path)) {
      recentGetResponses.delete(key);
    }
  }
};

export const showProfile = async () => {
  const token = getAuthToken();
  const key = `GET:${urlBase}/auth/profile:${token}`;
  return await getJsonWithDedup(key, () =>
    fetch(`${urlBase}/auth/profile`, {
      headers: { Authorization: `Bearer ${token}` },
    })
  );
};

export const updateProfile = async (values: any) => {
  const token = getAuthToken();
  const resp = await fetch(`${urlBase}/auth/profile`, {
    method: "PATCH",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(values),
  });
  clearGetCacheByPath("/auth/profile");
  return await resp.json();
};

export const getInfo = async (uuid: string) => {
  const token = getAuthToken();
  const key = `GET:${urlBase}/examination/${uuid}:${token}`;
  return await getJsonWithDedup(key, () =>
    fetch(`${urlBase}/examination/${uuid}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
  );
};

export const listPatients = async (query: string) => {
  const token = getAuthToken();
  const url = `${urlBase}/examination?timezone=${
    Intl.DateTimeFormat().resolvedOptions().timeZone
  }${query ? `&${query}` : ""}`;
  const key = `GET:${url}:${token}`;
  return await getJsonWithDedup(key, () =>
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
  );
};

export const updatePassword = async (values: any) => {
  const token = localStorage.getItem("token");
  const resp = await fetch(`${urlBase}/auth/password`, {
    method: "PATCH",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(values),
  });
  return await resp.json();
};

export const listUbigeos = async () => {
  const token = getAuthToken();
  const key = `GET:${urlBase}/ubigeo:${token}`;
  const { data } = await getJsonWithDedup(key, () =>
    fetch(`${urlBase}/ubigeo`, {
      headers: { Authorization: `Bearer ${token}` },
    })
  );
  return data?.map((item: { code: string; district: string }) => ({
    value: item.code,
    label: item.district,
  }));
};

export const listHeadquarters = async () => {
  const token = getAuthToken();
  const key = `GET:${urlBase}/headquarter/active:${token}`;
  const { data } = await getJsonWithDedup(key, () =>
    fetch(`${urlBase}/headquarter/active`, {
      headers: { Authorization: `Bearer ${token}` },
    })
  );
  return data?.map((item: { uuid: string; name: string }) => ({
    value: item.uuid,
    label: item.name,
  }));
};

export const listActiveTypeDiagnostics = async () => {
  const key = `GET:${urlBase}/type-doctor/active`;
  const { data } = await getJsonWithDedup(key, () =>
    fetch(`${urlBase}/type-doctor/active`, {
      headers: { accept: "application/json" },
    })
  );
  if (!Array.isArray(data)) return [];

  return data
    .map((item: any) => ({
      value: item.uuid ,
      label: item.name,
    }))
    .filter((item: { value: string; label: string }) => item.value && item.label);
};

export const findPatientByDNI = async (
  dni: string
): Promise<IPatient | null> => {
  const token = getAuthToken();
  const url = `${urlBase}/patient/search?identificationNumber=${dni}`;
  const key = `GET:${url}:${token}`;
  const { data } = await getJsonWithDedup(key, () =>
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
  );
  return data;
};

export const createPatient = async (values: any) => {
  const token = localStorage.getItem("token");
  values.identificationNumber = values.dni;
  values.gender = Number(values.gender);
  delete values.dni;
  const resp = await fetch(`${urlBase}/patient`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(values),
  });
  return await resp.json();
};

export const createDoctor = async (values: {
  name: string;
  lastName: string;
  email: string;
  phone: string;
  identificationNumber: string;
  typeDoctor: string;
  birthdate: string;
  password: string;
  token: string;
  code?: string;
}) => {
  const resp = await fetch(`${urlBase}/doctor`, {
    method: "POST",
    headers: {
      accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify(values),
  });

  const response = await resp.json().catch(() => ({}));

  if (!resp.ok) {
    throw new Error(response.message || `Error HTTP ${resp.status}`);
  }

  return response;
};

export const createOrder = async (values: any) => {
  const token = localStorage.getItem("token");
  const body = { ...values };

  if (body.tomography3d) {
    const tomography3d = { ...body.tomography3d };

    if (!tomography3d.largeField) {
      delete tomography3d.largeField;
    }
    if (!tomography3d.smallField?.exam) {
      delete tomography3d.smallField;
    }
    if (!tomography3d.mediumField?.exam) {
      delete tomography3d.mediumField;
    }
    if (!tomography3d.additionalNotes) {
      delete tomography3d.additionalNotes;
    }

    body.tomography3d = tomography3d;
  }

  const resp = await fetch(`${urlBase}/order`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  });
  return await resp.json();
};

export const listOrders = async (query: string) => {
  const token = getAuthToken();
  const url = `${urlBase}/order?timezone=${
    Intl.DateTimeFormat().resolvedOptions().timeZone
  }${query ? `&${query}` : ""}`;
  const key = `GET:${url}:${token}`;
  const { data } = await getJsonWithDedup(key, () =>
    fetch(url, {
      headers: { Authorization: `Bearer ${token}` },
    })
  );
  return data;
};

export const showOrder = async (uuid: string) => {
  try {
    const token = getAuthToken();
    const key = `GET:${urlBase}/order/${uuid}:${token}`;
    const { data } = await getJsonWithDedup(key, () =>
      fetch(`${urlBase}/order/${uuid}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
    );
    return data;
  } catch (error) {
    console.log(error);
    return error;
  }
};

export const downloadOrder = async (uuid: string) => {
  const token = localStorage.getItem("token");
  return await fetch(`${urlBase}/order/${uuid}/download`, {
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/pdf",
    },
  });
};

export const sendOrderWhatsapp = async (uuid: string, phone: string) => {
  const token = localStorage.getItem("token");
  const resp = await fetch(`${urlBase}/order/${uuid}/whatsapp`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ phone }),
  });
  return await resp.json();
};
